import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiTrendingUp, FiPlus, FiAward, FiTarget, FiClock, FiUsers, FiCheck,
  FiX, FiEdit3, FiTrash2, FiPlay, FiPause, FiCalendar, FiBarChart2,
  FiTrophy, FiZap, FiStar, FiGift, FiActivity, FiFilter, FiSearch
} = FiIcons;

const Challenges = () => {
  const [activeTab, setActiveTab] = useState('active');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const challenges = [
    {
      id: 1,
      title: '30-Day Referral Streak',
      description: 'Make at least one referral every day for 30 consecutive days',
      type: 'streak',
      status: 'active',
      participants: 156,
      completions: 23,
      reward: 500,
      duration: '30 days',
      startDate: '2024-01-01',
      endDate: '2024-01-31',
      progress: 67,
      icon: FiZap,
      color: 'purple'
    },
    {
      id: 2,
      title: 'First 10 Referrals',
      description: 'Complete your first 10 successful patient referrals',
      type: 'milestone',
      status: 'active',
      participants: 234,
      completions: 89,
      reward: 200,
      duration: 'No time limit',
      startDate: '2024-01-01',
      endDate: null,
      progress: 45,
      icon: FiTarget,
      color: 'blue'
    },
    {
      id: 3,
      title: 'Quality Reviews Champion',
      description: 'Maintain a 5-star average rating for 20 completed cases',
      type: 'quality',
      status: 'active',
      participants: 187,
      completions: 34,
      reward: 750,
      duration: 'Ongoing',
      startDate: '2024-01-01',
      endDate: null,
      progress: 82,
      icon: FiStar,
      color: 'yellow'
    },
    {
      id: 4,
      title: 'Weekend Warrior',
      description: 'Complete 5 referrals during weekends this month',
      type: 'time-based',
      status: 'active',
      participants: 98,
      completions: 12,
      reward: 300,
      duration: '1 month',
      startDate: '2024-01-01',
      endDate: '2024-01-31',
      progress: 34,
      icon: FiCalendar,
      color: 'green'
    },
    {
      id: 5,
      title: 'December Referral Master',
      description: 'Top referrer of December 2023',
      type: 'competition',
      status: 'completed',
      participants: 412,
      completions: 1,
      reward: 1000,
      duration: '1 month',
      startDate: '2023-12-01',
      endDate: '2023-12-31',
      progress: 100,
      icon: FiTrophy,
      color: 'orange'
    }
  ];

  const stats = {
    activeChallenges: challenges.filter(c => c.status === 'active').length,
    totalParticipants: challenges.reduce((sum, c) => sum + c.participants, 0),
    completionRate: 28.5,
    totalRewardsGiven: 45600
  };

  const getColorClasses = (color) => {
    const colors = {
      purple: 'bg-purple-50 border-purple-200 text-purple-600',
      blue: 'bg-blue-50 border-blue-200 text-blue-600',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-600',
      green: 'bg-green-50 border-green-200 text-green-600',
      orange: 'bg-orange-50 border-orange-200 text-orange-600'
    };
    return colors[color] || colors.blue;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredChallenges = challenges.filter(challenge => {
    const matchesSearch = challenge.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         challenge.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTab = activeTab === 'all' || challenge.status === activeTab;
    return matchesSearch && matchesTab;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Individual Challenges</h1>
              <p className="text-gray-600 mt-1">Engage users with personal achievement challenges</p>
            </div>
            <div className="mt-4 sm:mt-0">
              <button 
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>Create Challenge</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-50 rounded-lg">
                <SafeIcon icon={FiActivity} className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+12%</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.activeChallenges}</p>
            <p className="text-sm text-gray-600 mt-1">Active Challenges</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <SafeIcon icon={FiUsers} className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+23%</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.totalParticipants}</p>
            <p className="text-sm text-gray-600 mt-1">Total Participants</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-50 rounded-lg">
                <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+8%</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.completionRate}%</p>
            <p className="text-sm text-gray-600 mt-1">Completion Rate</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-orange-50 rounded-lg">
                <SafeIcon icon={FiGift} className="w-6 h-6 text-orange-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+15%</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">${stats.totalRewardsGiven.toLocaleString()}</p>
            <p className="text-sm text-gray-600 mt-1">Rewards Given</p>
          </motion.div>
        </div>

        {/* Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search challenges..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent w-64"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {['all', 'active', 'completed', 'draft'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeTab === tab
                      ? 'bg-purple-50 text-purple-600 border border-purple-200'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Challenges Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredChallenges.map((challenge, index) => (
            <motion.div
              key={challenge.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 + index * 0.1 }}
              className={`bg-white rounded-xl shadow-sm border-2 overflow-hidden hover:shadow-md transition-all ${getColorClasses(challenge.color)}`}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${getColorClasses(challenge.color)}`}>
                    <SafeIcon icon={challenge.icon} className="w-6 h-6" />
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(challenge.status)}`}>
                    {challenge.status}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2">{challenge.title}</h3>
                <p className="text-sm text-gray-600 mb-4">{challenge.description}</p>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <SafeIcon icon={FiUsers} className="w-4 h-4" />
                      <span>{challenge.participants} participants</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <SafeIcon icon={FiCheck} className="w-4 h-4" />
                      <span>{challenge.completions} completed</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <SafeIcon icon={FiClock} className="w-4 h-4" />
                      <span>{challenge.duration}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-purple-600 font-semibold">
                      <SafeIcon icon={FiGift} className="w-4 h-4" />
                      <span>{challenge.reward} points</span>
                    </div>
                  </div>
                </div>

                {challenge.status === 'active' && (
                  <div>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-600">Progress</span>
                      <span className="font-semibold text-gray-900">{challenge.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${challenge.color === 'purple' ? 'bg-purple-500' : challenge.color === 'blue' ? 'bg-blue-500' : challenge.color === 'yellow' ? 'bg-yellow-500' : challenge.color === 'green' ? 'bg-green-500' : 'bg-orange-500'}`}
                        style={{ width: `${challenge.progress}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-gray-200">
                  <button className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2">
                    <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                    <span>Edit</span>
                  </button>
                  <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                    {challenge.status === 'active' ? (
                      <SafeIcon icon={FiPause} className="w-5 h-5" />
                    ) : (
                      <SafeIcon icon={FiPlay} className="w-5 h-5" />
                    )}
                  </button>
                  <button className="p-2 text-gray-400 hover:text-red-600 transition-colors">
                    <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Create Challenge Modal */}
        <AnimatePresence>
          {showCreateModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowCreateModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
              >
                <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">Create New Challenge</h3>
                      <p className="text-sm text-gray-500 mt-1">Set up a new challenge for your users</p>
                    </div>
                    <button
                      onClick={() => setShowCreateModal(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Challenge Title *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., 30-Day Referral Streak"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description *
                    </label>
                    <textarea
                      placeholder="Describe the challenge..."
                      rows={3}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Challenge Type *
                      </label>
                      <select className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                        <option value="streak">Streak</option>
                        <option value="milestone">Milestone</option>
                        <option value="quality">Quality</option>
                        <option value="time-based">Time-Based</option>
                        <option value="competition">Competition</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Reward Points *
                      </label>
                      <input
                        type="number"
                        placeholder="500"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Start Date
                      </label>
                      <input
                        type="date"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        End Date
                      </label>
                      <input
                        type="date"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Goal/Target
                    </label>
                    <input
                      type="number"
                      placeholder="e.g., 30 referrals"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="py-2.5 px-5 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button className="py-2.5 px-6 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors">
                    Create Challenge
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Challenges;